import { isAfter } from "date-fns"
import { Action, Module, Mutation } from "vuex-class-modules"

import { BaseModule } from "@/models/BaseModule"
import {
  CreateTimesheet,
  ItemTimesheet,
  MonthIntervals,
  Timesheet,
  TimesheetChangeHistory,
  TimesheetChangeHistoryFilter,
  TimesheetFilter,
  TimesheetSimplify,
  TimesheetStats,
  TimesheetSummary,
  TimesheetSummaryDowlandParams, TimesheetSummaryWeeks,
  TimesheetWeeks,
  TimesheetWeeksDTO,
  WeekInterval,
  WeeksIntervals
} from "@/models/Timesheet"

import {
  TimesheetHistoryItem,
} from "@/models/TimesheetItems"
import {
  AlertContentItem,
  AlertType,
  Filter,
  ServerResponse,
  User
} from "@/models"
import { accountModule } from "./account.module"
import { apiService } from "@/services"
import { Endpoint } from "@/services/endpoints"
import { AlertActions, alertModule } from "./alert.module"
import { $t } from "@/plugins/i18n"
import { parsingServerResponseContent } from "@/utils/helpers"

import { Modules } from "../modules"
import { store } from "../store"
import fileSaver from "@/utils/fileSaver"


export enum TimesheetActions {
  GET_TIME_SHEET = "GET_TIME_SHEET",
  TIME_SHEET_ENTRIES_SAVE = "TIME_SHEET_ENTRIES_SAVE",
  TIME_SHEET_SUBMIT = "TIME_SHEET_SUBMIT",
  SEARCH_TIME_SHEETS = "SEARCH_TIME_SHEETS",
  CREATE_TIME_SHEET = "CREATE_TIME_SHEET",
  GET_TIME_SHEET_STATS = "GET_TIME_SHEET_STATS",
  GET_TIME_SHEETS = "GET_TIME_SHEETS",
  GET_TIME_SHEET_SUMMARY = "GET_TIME_SHEET_SUMMARY",
  GET_TIME_SHEET_SUMMARY_WEEKS = "GET_TIME_SHEET_SUMMARY_WEEKS",
  TIME_SHEET_DECLINE = "TIME_SHEET_DECLINE",
  TIME_SHEET_APPROVE = "TIME_SHEET_APPROVE",
  TIME_SHEET_CURRENT = "TIME_SHEET_CURRENT",
  ADD_ITEM_IN_LIST = "ADD_ITEM_IN_LIST",
  GET_ENABLED_USERS = "GET_ENABLED_USERS",
  GET_WEEKS_INTERVALS = "GET_WEEKS_INTERVALS",
  GET_MONTH_INTERVALS = "GET_MONTH_INTERVALS",
  SET_FILTERED_ENABLED_USERS = "SET_FILTERED_ENABLED_USERS",
  UPDATE_TIME_SHEET_ITEM_VALUE = "UPDATE_TIME_SHEET_ITEM_VALUE",
  REMOVE_ITEM_FROM_LIST = "REMOVE_ITEM_FROM_LIST",
  RESET_TIMESHEET = "RESET_TIMESHEET",
  CALCULATE_TIMESHEET_FOOTER = "CALCULATE_TIMESHEET_FOOTER",
  GET_TIME_SHEET_SUMMARY_EXCEL = "GET_TIME_SHEET_SUMMARY_EXCEL",
  TIME_SHEET_CHANGE_HISTORY = "TIME_SHEET_CHANGE_HISTORY",
  RESET_TIMESHEET_HISTORY = "RESET_TIMESHEET_HISTORY",
  SET_TIMESHEET_IN_LIST = "SET_TIMESHEET_IN_LIST",
  SET_SIMPLIFY_TIMESHEET_IN_LIST = "SET_SIMPLIFY_TIMESHEET_IN_LIST",
  TIME_SHEET_PERIOD_AVAILABLE_WEEKS = "TIME_SHEET_PERIOD_AVAILABLE_WEEKS"
}

@Module
class TimesheetModule extends BaseModule {
  timesheet = new Timesheet()
  timesheetSummary: TimesheetSummary[] = []
  timesheetSummaryWeeks: TimesheetSummaryWeeks[] = []
  timesheetsResponse: ServerResponse<Timesheet>
  simplifyTimesheetsResponse: ServerResponse<TimesheetSimplify>
  weeksIntervals: WeeksIntervals
  monthIntervals: MonthIntervals
  timesheetStats: TimesheetStats = new TimesheetStats()
  enabledUsers: User[] = []
  timesheetChangeHistory: ServerResponse<TimesheetChangeHistory> = new ServerResponse<TimesheetChangeHistory>()
  
  get accountId() {
    return accountModule.accountId
  }

  get [`get/${TimesheetActions.GET_TIME_SHEET}`]() {
    return this.timesheet
  }

  get [`get/${TimesheetActions.GET_ENABLED_USERS}`]() {
    return this.enabledUsers
  }

  get [`get/${TimesheetActions.SEARCH_TIME_SHEETS}`]() {
    return this.simplifyTimesheetsResponse
  }

  get [`get/${TimesheetActions.GET_TIME_SHEETS}`]() {
    return this.timesheetsResponse
  }

  get [`get/${TimesheetActions.GET_TIME_SHEET_STATS}`]() {
    return this.timesheetStats
  }

  get [`get/${TimesheetActions.GET_TIME_SHEET_SUMMARY}`]() {
    return this.timesheetSummary
  }

  get [`get/${TimesheetActions.GET_WEEKS_INTERVALS}`]() {
    return this.weeksIntervals
  }

  get [`get/${TimesheetActions.GET_MONTH_INTERVALS}`]() {
    return this.monthIntervals
  }

  get [`get/${TimesheetActions.GET_TIME_SHEET_SUMMARY_WEEKS}`]() {
    return this.timesheetSummaryWeeks
  }

  get [`get/${TimesheetActions.TIME_SHEET_CHANGE_HISTORY}`]() {
    return this.timesheetChangeHistory
  }

  @Mutation
  setTimesheetChangeHistory(timesheetChangeHistory: ServerResponse<TimesheetChangeHistory>) {
    const content = parsingServerResponseContent({
      originalContent: this.timesheetChangeHistory.content,
      serverResponseContent: timesheetChangeHistory.content,
      numberOfPage: timesheetChangeHistory.pageable.pageNumber,
    })
    this.timesheetChangeHistory = { ...timesheetChangeHistory, content }
  }

  @Mutation
  setTimesheetValue({ id, value }: { id: string; value: any }) {
    this.timesheet[id] = value
  }

  @Mutation
  setEnabledUsers(enabledUsers: User[]) {
    this.enabledUsers = enabledUsers
  }

  @Mutation
  setTimesheet(timesheet: Timesheet) {
    this.timesheet = timesheet
  }

  @Mutation
  setTimesheetStats(timesheetStats: TimesheetStats) {
    this.timesheetStats = timesheetStats
  }

  @Mutation
  setWeeksIntervals(weeksIntervals: WeeksIntervals) {
    this.weeksIntervals = weeksIntervals
  }

  @Mutation
  setMonthIntervals(monthIntervals: MonthIntervals) {
    this.monthIntervals = monthIntervals
  }

  @Mutation
  setTimesheetEntries(itemsTimesheet: ItemTimesheet[]) {
    this.timesheet.entries = itemsTimesheet
  }

  @Mutation
  setTimesheetsResponse(timesheetsResponse: ServerResponse<Timesheet>) {
    this.timesheetsResponse = timesheetsResponse
  }

  @Mutation
  setSimplifyTimesheetsResponse(simplifyTimesheetsResponse: ServerResponse<TimesheetSimplify>) {
    this.simplifyTimesheetsResponse = simplifyTimesheetsResponse
  }

  @Mutation
  setTimesheetSummary(timesheetSummary: TimesheetSummary[]) {
    this.timesheetSummary = timesheetSummary
  }

  @Mutation
  setTimesheetSummaryWeeks(timesheetSummaryWeeks: TimesheetSummaryWeeks[]) {
    this.timesheetSummaryWeeks = timesheetSummaryWeeks
  }

  @Mutation
  addItemInList(item: ItemTimesheet) {
    this.timesheet.entries.push(item)
    this.timesheet.calculateMetrics()
  }

  @Mutation
  resetTimesheet() {
    this.timesheet = new Timesheet()
  }

  @Mutation
  resetTimesheetHistory() {
    this.timesheetChangeHistory = new ServerResponse<TimesheetChangeHistory>()
  }

  @Mutation
  setTimesheetInList(timesheet: Timesheet) {
    if (!this.timesheetsResponse) return
    const finIndex = this.timesheetsResponse.content.findIndex(item => item.id === timesheet.id)
    if (finIndex >= 0) {
      Object.assign(this.timesheetsResponse.content[finIndex], timesheet)
      return
    }
    const index = this.timesheetsResponse.content.findIndex(item =>
      isAfter(new Date(timesheet.endDate), new Date(item.startDate))
    )
    if (index === -1) {
      this.timesheetsResponse.content.unshift(timesheet)
    } else {
      this.timesheetsResponse.content.splice(index, 0, timesheet)
    }
  }

  setSimplifyTimesheetInList(timesheet: TimesheetSimplify) {
    if(!this.simplifyTimesheetsResponse) return
    const finIndex = this.simplifyTimesheetsResponse.content.findIndex(item => item.id === timesheet.id)
    if (finIndex >= 0) {
      Object.assign(this.simplifyTimesheetsResponse.content[finIndex], timesheet)
      return
    }
    const index = this.simplifyTimesheetsResponse.content.findIndex(item =>
      isAfter(new Date(timesheet.endDate), new Date(item.startDate))
    )
    if (index === -1) {
      this.simplifyTimesheetsResponse.content.unshift(timesheet)
    } else {
      this.simplifyTimesheetsResponse.content.splice(index, 0, timesheet)
    }
  }

  @Mutation
  updateItemValueInList({ index, id, value }: { index: number; id: string; value: any }) {
    if (!this.timesheet.entries?.[index]) return
    this.timesheet.entries[index][id] = value
    this.timesheet.calculateMetrics()
  }

  @Mutation
  removeItemFromList(index: number) {
    this.timesheet.entries.splice(index, 1)
    this.timesheet.calculateMetrics()
  }

  @Mutation
  calculateTimesheetFooter(projectId?: string) {
    this.timesheet.calculateMetrics(projectId)
  }

  @Mutation
  refreshItemStatusInList(timesheet: Timesheet) {
    if (this.simplifyTimesheetsResponse?.content.length) {
      const simplifyIndex = this.simplifyTimesheetsResponse.content.findIndex(
        (item) => item.id === timesheet.id
      );
      if (simplifyIndex !== -1) {
        this.simplifyTimesheetsResponse.content[simplifyIndex].status = timesheet.status;
      }
    }

    if (this.timesheetsResponse?.content.length) {
      const timesheetIndex = this.timesheetsResponse.content.findIndex(
        (item) => item.id === timesheet.id
      );
      if (timesheetIndex !== -1) {
        this.timesheetsResponse.content[timesheetIndex].status = timesheet.status;
      }
    }
  }

  @Action
  async [TimesheetActions.CALCULATE_TIMESHEET_FOOTER](projectId?: string) {
    this.calculateTimesheetFooter(projectId)
  }

  @Action
  async [TimesheetActions.TIME_SHEET_PERIOD_AVAILABLE_WEEKS]() {
    const data = await apiService.get<TimesheetWeeksDTO[]>(
      Endpoint.TIME_SHEET_PERIOD_AVAILABLE_WEEKS(this.accountId),
    )
    return this.handleResponse<TimesheetWeeksDTO[]>(data, data => {
      const createdWeeksIntervals = data.map(item => new TimesheetWeeks(item))
      return createdWeeksIntervals
    })
  }

  @Action
  async [TimesheetActions.GET_TIME_SHEETS]({
    filter,
    withoutUpdate
  }: {
    filter: Filter
    withoutUpdate?: boolean
  }) {
    const filterJsonObj = filter.getJsonObj()
    const data = await apiService.get<ServerResponse<TimesheetSimplify>>(
      Endpoint.TIME_SHEET(this.accountId),
      filterJsonObj
    )
    return this.handleResponse<ServerResponse<TimesheetSimplify>>(data, data => {
      const serverResponseContent = data.content.map(item => new TimesheetSimplify(item))
      const content: Timesheet[] = parsingServerResponseContent({
        originalContent: this.timesheetsResponse?.content ?? [],
        serverResponseContent,
        numberOfPage: data.number
      })
      if (!withoutUpdate) {
        this.setTimesheetsResponse({ ...data, content })
      }
      return { ...data, content }
    })
  }

  @Action
  async [TimesheetActions.GET_TIME_SHEET]({
    timesheetId,
    withoutUpdate
  }: {
    timesheetId: string
    withoutUpdate?: boolean
  }) {
    const data = await apiService.get<Timesheet>(
      Endpoint.GET_TIME_SHEET(this.accountId, timesheetId)
    )
    return this.handleResponse<Timesheet>(data, data => {
      const newTimesheet = new Timesheet(data)
      if (!withoutUpdate) {
        this.setTimesheet(newTimesheet)
      }
      return newTimesheet
    })
  }

  @Action
  async [TimesheetActions.TIME_SHEET_CURRENT](withoutUpdate?: boolean) {
    const data = await apiService.get<Timesheet>(Endpoint.TIME_SHEET_CURRENT(this.accountId))
    return this.handleResponse<Timesheet>(data, data => {
      const newTimesheet = new Timesheet(data)
      if (!withoutUpdate) {
        this.setTimesheet(newTimesheet)
      }
      return newTimesheet
    })
  }

  @Action
  async [TimesheetActions.TIME_SHEET_ENTRIES_SAVE]({
    timesheetId,
    entries,
    withoutUpdate
  }: {
    timesheetId: string
    entries: ItemTimesheet[]
    withoutUpdate?: boolean
  }) {
    const entriesDTO = entries.map(item => item.getJsonObj())
    const data = await apiService.put<Timesheet>(
      Endpoint.TIME_SHEET_ENTRIES(this.accountId, timesheetId),
      entriesDTO
    )
    return this.handleResponse<Timesheet>(data, data => {
      const timesheet = new Timesheet(data)
      if (!withoutUpdate) {
        this.setTimesheet(timesheet)
      }
      this.refreshItemStatusInList(timesheet)
      const alertContent: AlertContentItem[] = [
        {
          text: "Timesheet ",
          type: "bold"
        },
        {
          text: $t("alert.saved"),
          type: "regular"
        }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      return timesheet
    })
  }

  @Action
  async [TimesheetActions.TIME_SHEET_SUBMIT]({
    timesheetId,
    entries,
    withoutUpdate
  }: {
    timesheetId: string
    entries: ItemTimesheet[]
    withoutUpdate?: boolean
  }) {
    const entriesDTO = entries.map(item => item.getJsonObj())
    const data = await apiService.post<Timesheet>(
      Endpoint.TIME_SHEET_SUBMIT(this.accountId, timesheetId),
      entriesDTO
    )
    return this.handleResponse<Timesheet>(data, data => {
      const timesheet = new Timesheet(data)
      if (!withoutUpdate) {
        this.setTimesheet(timesheet)
      }
      this.refreshItemStatusInList(timesheet)
      const alertContent: AlertContentItem[] = [
        {
          text: "Timesheet ",
          type: "bold"
        },
        {
          text: $t("alert.submitted"),
          type: "regular"
        }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      return timesheet
    })
  }

  
  @Action
  async [TimesheetActions.SEARCH_TIME_SHEETS]({
    filter,
    withoutUpdate
  }: {
    filter: TimesheetFilter
    withoutUpdate?: boolean
  }) {
    const filterJsonObj = filter.getJsonObj()
    const data = await apiService.get<ServerResponse<TimesheetSimplify>>(
      Endpoint.TIME_SHEET_SEARCH(this.accountId),
      filterJsonObj
    )
    return this.handleResponse<ServerResponse<TimesheetSimplify>>(data, data => {
      const serverResponseContent = data.content.map(item => new TimesheetSimplify(item))
      const content: TimesheetSimplify[] = parsingServerResponseContent({
        originalContent: this.simplifyTimesheetsResponse?.content ?? [],
        serverResponseContent,
        numberOfPage: data.number
      })
      if (!withoutUpdate) {
        this.setSimplifyTimesheetsResponse({ ...data, content })
      }
      return { ...data, content }
    })
  }

  @Action
  async [TimesheetActions.CREATE_TIME_SHEET]({
    createTimesheet,
    withoutUpdate
  }: {
    createTimesheet: CreateTimesheet
    withoutUpdate?: boolean
  }) {
    const data = await apiService.post<Timesheet>(
      Endpoint.TIME_SHEET(this.accountId),
      createTimesheet.getJsonObj()
    )
    return this.handleResponse<Timesheet>(data, data => {
      const createdTimesheet = new Timesheet(data)
      if (!withoutUpdate) {
        this.setTimesheet(createdTimesheet)
      }
      const alertContent: AlertContentItem[] = [
        {
          text: "Timesheet ",
          type: "bold"
        },
        {
          text: $t("alert.created"),
          type: "regular"
        }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      return createdTimesheet
    })
  }

  
  @Action
  async [TimesheetActions.GET_TIME_SHEET_STATS]({
    filter,
    withoutUpdate
  }: {
    filter: TimesheetFilter
    withoutUpdate?: boolean
  }) {
    const filterJsonObj = filter.getJsonObj()
    const data = await apiService.get<TimesheetStats>(
      Endpoint.TIME_SHEET_STATS(this.accountId),
      filterJsonObj
    )
    return this.handleResponse<TimesheetStats>(data, data => {
      const createdTimesheetStats = new TimesheetStats(data)
      if (!withoutUpdate) {
        this.setTimesheetStats(createdTimesheetStats)
      }
      return createdTimesheetStats
    })
  }

  
  @Action
  async [TimesheetActions.GET_WEEKS_INTERVALS]() {
    const data = await apiService.get<WeekInterval[]>(
      Endpoint.TIME_SHEET_PERIOD_WEEKS(this.accountId)
    )
    return this.handleResponse<WeekInterval[]>(data, data => {
      const createdWeeksIntervals = new WeeksIntervals(data);
      this.setWeeksIntervals(createdWeeksIntervals);
      return createdWeeksIntervals;
    })
  }

  
  @Action
  async [TimesheetActions.GET_MONTH_INTERVALS]() {
    const data = await apiService.get<string[]>(
      Endpoint.TIME_SHEET_PERIOD_MONTH(this.accountId)
    )
    return this.handleResponse<string[]>(data, data => {
      const createdMonthIntervals = new MonthIntervals(data);
      this.setMonthIntervals(createdMonthIntervals);
      return createdMonthIntervals;
    })
  }

  
  @Action
  async [TimesheetActions.GET_TIME_SHEET_SUMMARY]({
    month,
    projectId,
    withoutUpdate
  }: {
    month: string
    projectId?: string
    withoutUpdate?: boolean
  }) {
    const data = await apiService.get<TimesheetSummary[]>(
      Endpoint.TIME_SHEET_SUMMARY(this.accountId),
      { month, projectId }
    )
    return this.handleResponse<TimesheetSummary[]>(data, data => {
      const timesheetSummary = data.map(item => new TimesheetSummary(item))
      if (!withoutUpdate) {
        this.setTimesheetSummary(timesheetSummary)
      }
      return timesheetSummary
    })
  }

  
  @Action
  async [TimesheetActions.GET_TIME_SHEET_SUMMARY_WEEKS]({
     month,
     projectId,
     withoutUpdate
    }: {
    month: string
    projectId?: string
    withoutUpdate?: boolean
  }) {
    const data = await apiService.get<TimesheetSummaryWeeks[]>(
      Endpoint.TIME_SHEET_SUMMARY_WEEKS(this.accountId),
      { month, projectId }
    )
    return this.handleResponse<TimesheetSummaryWeeks[]>(data, data => {
      const timesheetSummaryWeeks = data.map(item => new TimesheetSummaryWeeks(item))
      if (!withoutUpdate) {
        this.setTimesheetSummaryWeeks(timesheetSummaryWeeks)
      }
      return timesheetSummaryWeeks
    })
  }

  
  @Action
  async [TimesheetActions.GET_TIME_SHEET_SUMMARY_EXCEL](params: TimesheetSummaryDowlandParams) {
    const { month, project } = params

    const data = await apiService.getBlob<string>(
      Endpoint.GET_TIME_SHEET_SUMMARY_EXCEL(this.accountId),
      { month, projectId: project.id }
    )

    return this.handleResponse<string>(data, async data => {
      return await fileSaver(data, `${project.name}.xlsx`)
    })
  }

  
  @Action
  async [TimesheetActions.TIME_SHEET_DECLINE]({
    timesheetId,
    reason
  }: {
    timesheetId: string
    reason: string
  }) {
    const data = await apiService.patch<Timesheet>(
      Endpoint.TIME_SHEET_DECLINE(this.accountId, timesheetId),
      reason
    )
    return this.handleResponse<Timesheet>(data, (data) => {
      const alertContent: AlertContentItem[] = [
        {
          text: "Timesheet ",
          type: "bold"
        },
        {
          text: "Declined",
          type: "regular"
        }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })

      const newTimesheet = new Timesheet(data)
      this.setTimesheet(newTimesheet)
      return newTimesheet
    })
  }

  
  @Action
  async [TimesheetActions.TIME_SHEET_APPROVE]({ timesheetId }: { timesheetId: string }) {
    const data = await apiService.patch<Timesheet>(
      Endpoint.TIME_SHEET_APPROVE(this.accountId, timesheetId)
    )
    return this.handleResponse<Timesheet>(data, (data) => {
      const alertContent: AlertContentItem[] = [
        {
          text: "Timesheet ",
          type: "bold"
        },
        {
          text: "Approved",
          type: "regular"
        }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent
      })
      const newTimesheet = new Timesheet(data)
      this.setTimesheet(newTimesheet)
      this.refreshItemStatusInList(newTimesheet)
      return newTimesheet
    })
  }

  @Action
  async [TimesheetActions.ADD_ITEM_IN_LIST](item: ItemTimesheet) {
    this.addItemInList(item)
  }

  @Action
  async [TimesheetActions.UPDATE_TIME_SHEET_ITEM_VALUE](params: {
    index: number
    id: string
    value: any
  }) {
    this.updateItemValueInList(params)
  }

  @Action
  async [TimesheetActions.GET_ENABLED_USERS]() {
    const data = await apiService.get<ServerResponse<User>>(
      Endpoint.TIME_SHEET_USERS_ENABLED(this.accountId)
    )

    this.handleResponse<ServerResponse<User>>(data, (data) => {
      const content: User[] = data.content.map(c => User.create(c))
      this.setEnabledUsers(content)
    })
  }

  @Action
  async [TimesheetActions.TIME_SHEET_CHANGE_HISTORY]({ filter, withoutUpdate } : { filter: TimesheetChangeHistoryFilter,  withoutUpdate?: boolean}) {
    const timesheetId = filter.timesheetId
    const filterJsonObj = filter.getJsonObj()
    const data = await apiService.get<ServerResponse<TimesheetChangeHistory>>(
      Endpoint.TIME_SHEET_CHANGE_HISTORY(this.accountId, timesheetId),
      filterJsonObj
    )

    this.handleResponse<ServerResponse<TimesheetChangeHistory>>(data, (data) => {
      data.content = data.content.map(item => new TimesheetChangeHistory(item))
      if(!withoutUpdate) {
        this.setTimesheetChangeHistory(data)
      }
      return data
    })
  }

  @Action
  async [TimesheetActions.SET_FILTERED_ENABLED_USERS](users: User[]) {
    this.setEnabledUsers(users)
  }

  @Action
  async [TimesheetActions.REMOVE_ITEM_FROM_LIST](index: number) {
    this.removeItemFromList(index)
  }

  @Action
  async [TimesheetActions.RESET_TIMESHEET]() {
    this.resetTimesheet()
  }

  @Action
  async [TimesheetActions.SET_TIMESHEET_IN_LIST](timesheet: Timesheet) {
    this.setTimesheetInList(timesheet)
  }

  @Action
  async [TimesheetActions.SET_SIMPLIFY_TIMESHEET_IN_LIST](timesheet: TimesheetSimplify) {
    this.setSimplifyTimesheetInList(timesheet)
  }

  @Action
  async [TimesheetActions.RESET_TIMESHEET_HISTORY]() {
    this.resetTimesheetHistory()
  }
}

export const timesheet = new TimesheetModule({
  store,
  name: Modules.TIME_SHEET
})
